import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Globe, Phone, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from "./NewsTicker";
import SocialLinks, { SOCIAL_FIELDS } from "./SocialLinks";

// Every tailor's public detail-page URL is generated automatically from its
// name (no manual page creation needed): /tailoring/<name-slug>
export function tailoringSlug(name: string) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// For SEO/display: many listings (boutiques, alteration shops) don't have
// "Tailor" in their business name, which hurts on-page relevance for
// tailoring-intent searches. Append it when it's not already present.
export function tailoringDisplayName(name: string) {
  const n = name || "";
  return /tailor/i.test(n) ? n : `${n} Tailor`;
}

const TAILORING_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "description", label: "Description" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "phone", label: "Phone Number", type: "tel", placeholder: "(214) 555-0123" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface TailoringEntry {
  name: string;
  description: string;
  website: string;
  address: string;
  image: string;
  facebook?: string;
  phone?: string;
  // Optional longer SEO description shown only on the individual detail page.
  // The card grid on this page keeps using `description` for the short blurb.
  detailDescription?: string;
}

export const TAILORING_ENTRIES: TailoringEntry[] = [
  {
    name: "Sharada",
    description: "Switching and Alteration",
    website: "",
    address: "Frisco, TX",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784043113524-46vnec.png",
    phone: "(214) 425-8647",
  },
  {
    name: "Kapoor Tailor",
    description: "Indian Blouse Stitching & Alterations",
    detailDescription: "Kapoor Tailor in Frisco, TX specializes in Indian blouse stitching, saree blouse alterations, salwar suit stitching, Indian clothing alterations, and tailoring services. Find Kapoor Tailor's location, services, contact information, and directions on DallasDesiScoop.",
    website: "",
    address: "Frisco, TX",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784042007054-2wuxo6.png",
    facebook: "https://www.facebook.com/ghambhirtailor",
  },
  {
    name: "Veena Tailor",
    description: "Pant Hemming, Alterations & Custom Tailoring",
    website: "",
    address: "8655 Brookhollow Blvd, Frisco, TX 75034",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784311224017-ftx23.jpg",
    phone: "(508) 304-0330",
  },
  {
    name: "Shri Tirumala",
    description: "Clothing boutique",
    website: "",
    address: "Dallas, TX",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784380293419-cv5j6o.png",
    facebook: "https://www.facebook.com/share/17NX67rt1o/",
  },
  {
    name: "M & K Boutique",
    description: "Alteration Service",
    website: "",
    address: "Plano, TX 75025",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784423725116-6yetjk.png",
    phone: "214-899-3644",
  },
  {
    name: "Kee’s Tailor",
    description: "",
    detailDescription:
      "Kee’s Tailor provides professional tailoring, clothing alterations, and custom garment services in Frisco, Texas. Our experienced team brings over 30 years of experience in helping customers achieve the perfect fit for everyday clothing, special occasions, and weddings. Our Services Include: Wedding dress and bridal gown alterations Bridesmaid, prom, and homecoming dress alterations Suit, tuxedo, jacket, and formalwear alterations Pants hemming and clothing adjustments Indian clothing alterations Uniform alterations and logo sewing Custom garment modifications and repairs",
    website: "https://keestailorfrisco.com",
    address: "3520 Preston Rd #110, Frisco, TX 75034",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-10-19/ce7881e2e1fc.jpeg",
    phone: "972-377-2777",
  },
  {
    name: "STITCH & DRAPE",
    description: "Sewing Alterations & Saree Draping",
    website: "",
    address: "Plano, TX",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784424318359-rgn3ahc.jpg",
    phone: "214-970-4568",
  },
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&h=400&fit=crop";

function mapsUrl(e: TailoringEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address}`)}`;
}

// "View Location" only makes sense with a full street address (e.g. "8655
// Brookhollow Blvd, Frisco, TX 75034"). Listings that only have a city/state
// ("Frisco, TX") don't have enough detail for a useful map pin, so we hide
// the button for those rather than sending users to an imprecise city-level
// map search.
function hasFullStreetAddress(address?: string) {
  return !!address && /^\s*\d+\s+\S/.test(address);
}

export function TailoringCard({ entry, index }: { entry: TailoringEntry; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Link to={`/tailoring/${tailoringSlug(entry.name)}`} className="flex flex-col flex-1">
        <div className="w-full h-[180px] relative overflow-hidden bg-gray-100 flex items-center justify-center">
          <img
            src={entry.image || FALLBACK_IMAGE}
            alt={entry.name}
            className="w-full h-full object-contain"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
            }}
          />
        </div>
        <div className="p-1.5 pb-0.5 flex-1 flex flex-col justify-between">
          <div>
            <h3 className="text-[10px] font-bold text-[#082f6d] leading-tight truncate">
              {tailoringDisplayName(entry.name)}
            </h3>
            {entry.description && (
              <p className="text-[9px] text-gray-500 font-medium leading-snug mt-0.5">
                {entry.description}
              </p>
            )}
            {entry.address && (
              <div className="flex items-start gap-0.5 mt-0.5">
                <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
                <span className="text-[9px] text-gray-500 font-medium leading-snug truncate">
                  {entry.address}
                </span>
              </div>
            )}
          </div>
        </div>
      </Link>
      <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
        {entry.website && (
          <a
            href={entry.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-0.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <Globe className="w-2.5 h-2.5" /> Website
          </a>
        )}
        <SocialLinks item={{ ...entry, website: undefined, facebook: undefined }} variant="sleek" />
        {entry.phone && (
          <a
            href={`tel:${entry.phone.replace(/[^\d+]/g, "")}`}
            className="flex items-center justify-center gap-0.5 bg-[#51ba4f] hover:bg-[#45a943] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <Phone className="w-2.5 h-2.5" /> Call
          </a>
        )}
        {entry.facebook && (
          <a
            href={entry.facebook}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-1 bg-[#1877F2] hover:bg-[#0f5cc4] text-white text-[10px] font-bold py-1.5 rounded-sm transition-colors duration-200"
          >
            <Facebook className="w-3 h-3" /> Facebook
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Tailoring() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>("tailoring_listings", TAILORING_ENTRIES, (e, i) => slugify(e.name || String(i)));

  const filtered = mc.items.filter(
    (e) =>
      query.trim() === "" ||
      e.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Tailoring"
        placeholder="Search tailors & boutiques…"
        query={query}
        setQuery={setQuery}
        backgroundImage="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-14/ce3c07251973.png"
      />
      <NewsTicker />

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Top Indian, Pakistani & South Asian Tailoring & Alteration Services in Dallas–Fort Worth
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Discover the top Indian, Pakistani, and South Asian tailors and alteration services across DFW. Find professionals for custom stitching, clothing alterations, saree blouses, lehengas, salwar suits, sherwanis, bridal outfits, and traditional clothing.
            </p>
            <AdminAddBar mc={mc} fields={TAILORING_FIELDS} label="Add Tailor" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                {filtered.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={TAILORING_FIELDS} title="Tailor">
                    <TailoringCard entry={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No tailors match your search.
              </div>
            )}
            <div className="mt-6">
              <p className="text-[13px] text-gray-500 mb-2">
                Find tailors, alteration shops, custom dressmakers, and ethnic clothing specialists in Dallas, Frisco, Plano, Irving, McKinney, Allen, The Colony, Prosper, Lewisville, Carrollton, Coppell, Richardson, Flower Mound, Little Elm, and surrounding DFW communities.
              </p>
              <p className="text-[13px] text-gray-500">
                Whether you're looking for Indian tailors, Pakistani tailors, South Asian tailoring, saree blouse stitching, bridal alterations, custom stitching, or clothing alterations near you, DallasDesiScoop helps you discover trusted tailoring and alteration services across the Dallas–Fort Worth area.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
